import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Datepicker,
    Input,
    Flex,
    Text,
    Checkbox,
    Button,
    Dropdown,
    TextArea,
    Alert,
    ExclamationTriangleIcon,
    ListIcon,
    CalendarIcon,
    NotesIcon,
    MeetingTimeIcon,
    Loader,
} from '@fluentui/react-northstar';
import { app, dialog } from '@microsoft/teams-js';
import { getMeetingData } from '../../api/publicapp-api';
import Data from '../../constants/data';
import i18n from '../../i18n';
import * as moment from 'moment';
import './bookings-page.scss';
import { getStaffMembers, postAppointment, getBookingsById } from '../../api/bookings-api';
import { getUserSettings } from '../../api/user-api';

interface BookingsTakeOffProps { }

interface RouteParams {
    ancora?: string;
}

const BookingsTakeOff: React.FC<BookingsTakeOffProps> = () => {
    const { t } = useTranslation();
    const { ancora } = useParams<RouteParams>();

    const localizationStrings = {
        shortDays: i18n.language.includes("es") ? Data.shortDaysES : i18n.language.includes("en") ? Data.shortDaysEN : Data.shortDaysPT,
        months: i18n.language.includes("es") ? Data.monthFullNamesES : i18n.language.includes("en") ? Data.monthFullNamesEN : Data.monthFullNames,
    }

    const timezoneOptions = [
        {
            key: 'Central Brazilian Standard Time',
            header: '(UTC-04:00 Cuiabá)',
        },
        {
            key: 'E. South America Standard Time',
            header: '(UTC-03:00 Brasília)',
        },
    ];

    moment.locale('pt-br');

    const [loading, setLoading] = useState<boolean>(true);
    const [bookingId, setBookingId] = useState<string>();
    const [description, setDescription] = useState<string>('Folga');
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [startTime, setStartTime] = useState<string | null>(null);
    const [endTime, setEndTime] = useState<string | null>(null);
    const [allDay, setAllDay] = useState<boolean>(false);
    const [notes, setNotes] = useState<string>('');
    const [timeOptions, setTimeOptions] = useState<{ key: string; header: string }[]>([]);
    const [loadingScheduleAppointment, setLoadingScheduleAppointment] = useState<boolean>(false);
    const [scheduledAppointment, setScheduledAppointment] = useState<boolean>(false);

    const [userLoginHint, setUserLoginHint] = useState<string>();
    const [staffMembers, setStaffMembers] = useState<any[]>([]);
    const [staffMemberId, setStaffMemberId] = useState<string>();

    const [selectedTimezone, setSelectedTimezone] = useState(timezoneOptions[0]);

    const [error, setError] = useState<string>();

    const generateTimeOptions = (targetDate: Date): { key: string; header: string }[] => {
        const isToday = moment(targetDate).isSame(moment(), 'day');
        const now = moment();
        const dateStart = moment(targetDate).startOf('day');

        let startHour = 0;
        let startMinute = 0;

        let time = dateStart.clone().add(startHour, 'hours').add(startMinute, 'minutes');
        const endOfDay = moment(targetDate).endOf('day').startOf('minute');

        const options: { key: string; header: string }[] = [];

        while (time.isSameOrBefore(endOfDay)) {
            options.push({
                key: time.format('HH:mm'),
                header: time.format('HH:mm'),
            });
            time.add(30, 'minutes');
        }

        return options;
    };

    const buildAppointmentPayload = (start: string, end: string) => {
        return {
            startDateTime: {
                dateTime: start,
                timeZone: selectedTimezone?.key,
            },
            endDateTime: {
                dateTime: end,
                timeZone: selectedTimezone?.key,
            },
            endTime: end,
            bookingsId: bookingId,
            serviceName: description,
            serviceNotes: notes,
            serviceId: "7759fea6-a78b-4b89-ad68-c6548bc8476a",
            staffMemberIds: [staffMemberId],
            customers: null,
            isAllDay: allDay,
            isLocationOnline: false,
            price: 0,
            priceType: "Undefined",
        };
    };

    const staffMap = useMemo(() => {
        const map = new Map<string, any>();
        for (const member of staffMembers ?? []) {
            const email = member.emailAddress?.trim().toLowerCase();
            if (email) {
                map.set(email, member);
            }
        }

        return map;
    }, [staffMembers]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const result = await getUserSettings(ancora);

                if (result.data) {
                    console.log(result.data);
                    setUserLoginHint(result.data.email);
                }
            } catch (error) {
                console.error('Erro ao carregar usuário:', error);
                setError("Erro ao carregar usuário.");
            }
        }

        const fetchMeetingData = async () => {
            try {
                const result = await getMeetingData(ancora);

                if (result.data) {
                    setBookingId(result.data.bookingsId);
                    fetchBookings(result.data.bookingsId);
                }
            } catch (error) {
                console.error('Erro ao carregar meeting data:', error);
                setError("Erro ao obter informações da unidade.");
            }
        }

        const fetchBookings = async (bookingId: string) => {
            try {
                const result = await getBookingsById(bookingId);

                if (result.data) {
                    setSelectedTimezone(timezoneOptions.find(t => t.key === result.data.businessTimeZone));
                    fetchStaffMembers(result.data.bookingsId);
                } 
            } catch (error) {
                console.error('Erro ao carregar bookings:', error);
            }
        }

        const fetchStaffMembers = async (bookingId: string) => {
            try {
                const result = await getStaffMembers(bookingId);

                if (result.data) {
                    setStaffMembers(result.data);
                    setLoading(false);
                } else {
                    setError("Não foram encontrados atendentes para este serviço do Bookings.");
                }
            } catch (error) {
                console.error('Erro ao carregar staff members:', error);
                setError("Erro ao obter informações de atendentes.");
            }
        }

        app.initialize();
        app.getContext().then(async (context: app.Context) => {
            const now = moment();
            const start = now.clone().startOf('minute').add(30 - (now.minute() % 30), 'minutes');
            const end = start.clone().add(30, 'minutes');
            const userEmail = context?.user?.loginHint;

            setStartTime(start.format('HH:mm'));
            setEndTime(end.format('HH:mm'));
            setTimeOptions(generateTimeOptions(now.toDate()));
            setStartDate(now.toDate());
            setEndDate(now.toDate());

            await fetchUserData();
            await fetchMeetingData();

            //const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

            //if (browserTz === 'America/Cuiaba') {
            //    setSelectedTimezone(timezoneOptions[0]);
            //} else {
            //    setSelectedTimezone(timezoneOptions[1]);
            //}
        });
    }, []);

    useEffect(() => {
        setTimeOptions(generateTimeOptions(startDate));
    }, [startDate]);

    useEffect(() => {
        if (!userLoginHint || staffMap.size === 0) return;

        const normalizedEmail = userLoginHint.trim().toLowerCase();
        const matchedStaff = staffMap.get(normalizedEmail);

        if (matchedStaff) {
            setStaffMemberId(matchedStaff.id);
        }
    }, [userLoginHint, staffMap]);

    return (
        <div className="bookings-takeoff">
            {error &&
                <Alert danger content={error} icon={<ExclamationTriangleIcon outline />} />
            }

            {loading ? <Loader label="Carregando..." /> :
                <Flex column gap="gap.large">
                    <Flex gap="gap.medium" vAlign="center">
                        <ListIcon outline />
                        <Input
                            fluid
                            placeholder="Descrição do apontamento"
                            value={description}
                            onChange={(_, data) => setDescription(data.value)}
                        />
                    </Flex>


                    <Flex gap="gap.medium" vAlign="center">
                        <Flex gap="gap.medium" vAlign="start">
                            <CalendarIcon outline />
                            <Flex className="date-picker" column>
                                <Text content="Início" />
                                <Datepicker
                                    {...localizationStrings}
                                    selectedDate={startDate}
                                    onDateChange={(_, data) => {
                                        const date = data.value;
                                        if (date) {
                                            setStartDate(date);
                                            setEndDate(date);

                                            setTimeOptions(generateTimeOptions(date));
                                            setError(null);
                                        }
                                    }}
                                    firstDayOfWeek={0}
                                />
                            </Flex>
                        </Flex>


                        {!allDay && (
                            <Flex column>
                                <Text content=" " />
                                <Dropdown
                                    items={timeOptions}
                                    placeholder="Selecione"
                                    value={startTime}
                                    onChange={(_, data) => {
                                        const selectedKey = (data.value as any).key;
                                        setStartTime(selectedKey);
                                        setError(null);
                                    }}
                                />
                            </Flex>
                        )}
                    </Flex>

                    <Flex gap="gap.medium" vAlign="center">
                        <Flex gap="gap.medium" vAlign="start">
                            <CalendarIcon outline styles={{ opacity: 0 }} />
                            <Flex className="date-picker" column>
                                <Text content="Fim" />
                                <Datepicker
                                    {...localizationStrings}
                                    selectedDate={allDay ? startDate : endDate}
                                    onDateChange={(_, data) => {
                                        const date = data.value;
                                        if (date) setEndDate(date);
                                        setError(null);
                                    }}
                                    minDate={startDate}
                                    firstDayOfWeek={0}
                                />
                            </Flex>
                        </Flex>

                        {!allDay && (
                            <Flex column>
                                <Text content=" " />
                                <Dropdown
                                    items={timeOptions}
                                    placeholder="Selecione"
                                    value={endTime}
                                    onChange={(_, data) => {
                                        const selectedKey = (data.value as any).key;
                                        setEndTime(selectedKey);
                                        setError(null);
                                    }}
                                />
                            </Flex>
                        )}

                        <Checkbox
                            label="Dia todo"
                            checked={allDay}
                            onChange={(_, data) => {
                                setAllDay(data.checked ?? false);
                                if (data.checked) {
                                    setStartTime('00:00');
                                    setEndTime('00:00');
                                    setEndDate(moment(startDate).clone().add(1, 'day').toDate());
                                } else {
                                    const now = moment();
                                    const start = now.clone().startOf('minute').add(30 - (now.minute() % 30), 'minutes');
                                    const end = start.clone().add(30, 'minutes');

                                    setStartTime(start.format('HH:mm'));
                                    setEndTime(end.format('HH:mm'));
                                    setEndDate(startDate);
                                }
                            }}
                            toggle
                        />
                    </Flex>

                    <Flex styles={{ margin: '0 auto' }} gap="gap.smaller" vAlign="center">
                        <Text content={`Os horários estão no fuso horário de ${selectedTimezone?.header}`} />
                    </Flex>

                    <Flex gap="gap.medium" vAlign="center">
                        <NotesIcon outline />
                        <TextArea
                            placeholder="Observações"
                            value={notes}
                            onChange={(_, data) => setNotes(data.value)}
                            rows={3}
                            fluid
                        />
                    </Flex>

                    <Flex className="buttons" gap="gap.medium" vAlign="center" hAlign="end">
                        <Button
                            content="Fechar"
                            onClick={() => dialog.url.submit('cancel')}
                        />

                        <Button
                            className={`btn-takeoff ${loadingScheduleAppointment ? 'disabled' : ''} ${scheduledAppointment ? 'scheduled' : ''}`}
                            content={!scheduledAppointment ? "Agendar Folga" : "Folga agendada"}
                            icon={scheduledAppointment ? <MeetingTimeIcon outline /> : null}
                            primary
                            disabled={
                                !startDate ||
                                !endDate ||
                                (!allDay && (!startTime || !endTime)) ||
                                moment(startDate).isAfter(endDate) ||
                                !!error
                            }
                            loading={!!loadingScheduleAppointment}
                            onClick={async () => {
                                setLoadingScheduleAppointment(true);

                                const start = moment(startDate).format('YYYY-MM-DD') + 'T' + (startTime ?? '00:00');
                                const end = moment(endDate).format('YYYY-MM-DD') + 'T' + (endTime ?? '23:59');

                                const payload = buildAppointmentPayload(start, end);

                                try {
                                    const response = await postAppointment(payload);

                                    if (response.data) {
                                        setLoadingScheduleAppointment(false);
                                        setScheduledAppointment(true);
                                    } else {
                                        setError("Erro ao criar o agendamento, tente novamente.");
                                        setTimeout(() => setError(null), 5000);
                                        setLoadingScheduleAppointment(false);
                                    }
                                } catch (error) {
                                    console.error('Erro ao agendar folga:', error);
                                    setTimeout(() => setError(null), 5000);
                                    setError("Erro ao criar o agendamento, tente novamente.");
                                    setLoadingScheduleAppointment(false);
                                }
                            }}
                        />
                    </Flex>
                </Flex>
            }
        </div>
    );
};

export default BookingsTakeOff;
